import React from 'react';
import NoControlsVideo from './NoControlsVideo';

const APPLE_WEBKIT = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iOS = /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return iOS || (/Safari\//.test(ua) && !/Chrome|Chromium|Edg|OPR|Firefox|Android/.test(ua));
})();

/** The loading panda's sources for this browser (also used by VideoWatermarkProgressModal). */
export const PandaLoaderSources = () => (
  <>
    {APPLE_WEBKIT
      ? <source src="/assets/loading-panda.mov" type='video/mp4; codecs="hvc1"' />
      : <source src="/assets/loading-panda.webm" type="video/webm" />}
    <source src="/assets/loading-panda.mp4" type="video/mp4" />
  </>
);

/**
 * PandaLoader — looping panda video used as the brand loading indicator.
 *
 * variant:
 *   - "fullscreen": fixed overlay covering the viewport, with a backdrop
 *   - "page":       fills the parent (e.g. inside a route view), no backdrop
 *   - "inline":     fits where placed (use `size` to control)
 *
 * size: width/height of the video in px (only honored for "inline"; the
 *       fullscreen/page variants use a responsive clamp).
 *
 * Transparent everywhere: Safari and every iPhone/iPad browser (all WebKit) get the HEVC-alpha .mov, the rest the
 * VP9-alpha .webm — one source per browser, because WebKit can't play alpha webm and Mac Chrome claims hvc1 then
 * drops the alpha (same rule as onboarding/NOKit ClipSource). The .mp4 (white background) is only a last resort.
 *
 * NoControlsVideo keeps any browser from drawing its own play button or
 * control bar over it, and where autoplay is off (iOS Low Power Mode) the
 * poster — its first frame — stands in until the next tap starts it.
 */
const PandaLoader = ({
  variant = 'fullscreen',
  size = 160,
  message,
  subMessage,
  withBackdrop = true,
  className = '',
  style = {},
}) => {
  const isFullscreen = variant === 'fullscreen';
  const isPage = variant === 'page';

  const videoSize = isFullscreen
    ? '320px'
    : isPage
      ? 'clamp(200px, 26vh, 320px)'
      : `${size}px`;

  const containerStyle = isFullscreen
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: withBackdrop
          ? 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)'
          : 'transparent',
        isolation: 'isolate',
        fontFamily: "'Fredoka', sans-serif",
      }
    : isPage
      ? {
          width: '100%',
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Fredoka', sans-serif",
        }
      : {
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Fredoka', sans-serif",
        };

  return (
    <div className={className} style={{ ...containerStyle, ...style }} role="status" aria-live="polite">
      <NoControlsVideo
        autoPlay
        muted
        loop
        preload="auto"
        poster="/assets/loading-panda-poster.png"
        animated="/assets/loading-panda-loop.webp"
        aria-label="Loading"
        style={{
          width: videoSize,
          height: videoSize,
          objectFit: 'contain',
          background: 'transparent',
          pointerEvents: 'none',
        }}
      >
        <PandaLoaderSources />
      </NoControlsVideo>
      {message && (
        <h3
          style={{
            margin: isFullscreen ? '-0.25rem 0 0' : '14px 0 0',
            color: '#0065F8',
            fontFamily: isFullscreen ? "'Caveat', cursive" : "'Fredoka', sans-serif",
            fontWeight: 700,
            fontSize: isFullscreen ? '3rem' : 'clamp(1.05rem, 1.8vw, 1.35rem)',
            letterSpacing: isFullscreen ? '0.01em' : '-0.01em',
            lineHeight: 1.1,
            textAlign: 'center',
            maxWidth: 560,
            padding: '0 16px',
          }}
        >
          {message}
        </h3>
      )}
      {subMessage && (
        <p
          style={{
            margin: '8px 0 0',
            color: '#5A6B85',
            fontSize: 14,
            textAlign: 'center',
            maxWidth: 480,
            padding: '0 16px',
            lineHeight: 1.5,
          }}
        >
          {subMessage}
        </p>
      )}
    </div>
  );
};

export default PandaLoader;
